"use client";

import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../ui/components/Button';
import type { ProjectRichlistResult, ProjectRichlistCollection } from '../../api/projects';

interface ProjectRichlistTableProps {
  richlist: ProjectRichlistResult;
  projectName?: string;
}

/**
 * Format a point value for display
 * Removes unnecessary trailing zeros (e.g., 1.00 -> 1, 0.50 -> 0.5)
 * Note: Value may come as string from API (PostgreSQL DECIMAL)
 */
function formatPointValue(value: number | string): string {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  // If it's a whole number, display without decimals
  if (Number.isInteger(num)) {
    return num.toLocaleString();
  }
  // Otherwise, format with up to 2 decimal places
  return num.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

/**
 * Get wallet page URL
 */
function getWalletUrl(address: string): string {
  return `/wallets/${address}`;
}

/**
 * Export project richlist data to CSV and trigger download
 */
function exportProjectRichlistToCsv(richlist: ProjectRichlistResult, projectName?: string): void {
  const rows: string[] = [];
  const hasAnyPoints = richlist.total_points > 0;

  // Build header row
  const headers = ['Rank', 'Holder'];
  for (const collection of richlist.collections) {
    headers.push(`${collection.name} NFTs`);
    if (hasAnyPoints) headers.push(`${collection.name} Points`);
  }
  headers.push('Total NFTs');
  if (hasAnyPoints) headers.push('Total Points');
  rows.push(headers.join(','));

  // Add data rows
  for (const holder of richlist.holders) {
    const row: (string | number)[] = [holder.rank, holder.owner_address];

    // Add per-collection data
    for (const collection of richlist.collections) {
      const colData = holder.collections.find((c) => c.collection_id === collection.id);
      row.push(colData?.nft_count ?? 0);
      if (hasAnyPoints) row.push(colData?.total_points ?? 0);
    }

    row.push(holder.total_nft_count);
    if (hasAnyPoints) row.push(holder.total_points);
    rows.push(row.join(','));
  }

  // Create and trigger download
  const csv = rows.join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const filename = projectName
    ? `richlist-${projectName.toLowerCase().replace(/\s+/g, '-')}.csv`
    : 'project-richlist.csv';
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Collection header cell - links to collection richlist
 * Uses fixed width with text wrapping to handle long collection names
 */
function CollectionHeader({ collection }: { collection: ProjectRichlistCollection }) {
  const collectionPath = collection.slug || collection.id;
  return (
    <Link
      to={`/collections/${collectionPath}?tab=richlist`}
      className="text-xs text-center text-brand-700 hover:text-brand-600 underline break-words"
    >
      {collection.name}
    </Link>
  );
}

/**
 * ProjectRichlistTable component
 * Renders project-wide richlist with per-collection breakdown columns
 */
export function ProjectRichlistTable({ richlist, projectName }: ProjectRichlistTableProps) {
  const handleExportCsv = () => {
    exportProjectRichlistToCsv(richlist, projectName);
  };

  if (richlist.holders.length === 0) {
    return (
      <div className="flex items-center justify-center py-12 text-subtext-color">
        No holders found
      </div>
    );
  }

  const hasMultipleCollections = richlist.collections.length > 1;

  // Determine if the project has any points at all — if not, show NFT counts in collection columns
  const hasAnyPoints = richlist.total_points > 0;

  return (
    <div className="flex flex-col gap-4">
      {/* Summary stats with export button */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex gap-4 text-sm text-subtext-color">
          {hasAnyPoints && (
            <span>
              Total Points:{' '}
              <strong className="text-default-font">{formatPointValue(richlist.total_points)}</strong>
            </span>
          )}
          <span>
            Holders:{' '}
            <strong className="text-default-font">{richlist.total_holders.toLocaleString()}</strong>
          </span>
          <span>
            Collections:{' '}
            <strong className="text-default-font">{richlist.collections.length}</strong>
          </span>
        </div>
        <Button variant="neutral-secondary" size="small" onClick={handleExportCsv}>
          Export CSV
        </Button>
      </div>

      <div className="overflow-x-auto max-w-full">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-neutral-border">
              <th className="py-3 px-4 text-left text-caption font-caption text-subtext-color w-16">Rank</th>
              <th className="py-3 px-4 text-left text-caption font-caption text-subtext-color">Holder</th>
              {/* Per-collection columns - min-width for numbers, max-width for text wrapping */}
              {hasMultipleCollections &&
                richlist.collections.map((collection) => (
                  <th
                    key={collection.id}
                    className="py-3 px-4 text-center text-caption font-caption text-subtext-color"
                    style={{ minWidth: '70px', maxWidth: '140px' }}
                  >
                    <CollectionHeader collection={collection} />
                  </th>
                ))}
              <th className="py-3 px-4 text-center text-caption font-caption text-subtext-color w-20">NFTs</th>
              {hasAnyPoints && (
                <th className="py-3 px-4 text-center text-caption font-caption text-subtext-color w-24">Points</th>
              )}
            </tr>
          </thead>
          <tbody>
            {richlist.holders.map((holder) => (
              <tr key={holder.owner_address} className="border-b border-neutral-100">
                <td className="py-3 px-4 w-16">
                  <span className="text-body-bold font-body-bold text-default-font">
                    #{holder.rank}
                  </span>
                </td>
                <td className="py-3 px-4">
                  <Link
                    to={getWalletUrl(holder.owner_address)}
                    className="text-body font-body text-brand-700 hover:text-brand-600 underline font-mono text-sm"
                  >
                    {holder.owner_address}
                  </Link>
                </td>
                {/* Per-collection breakdown: show points if available, otherwise NFT counts */}
                {hasMultipleCollections &&
                  richlist.collections.map((collection) => {
                    const colData = holder.collections.find(
                      (c) => c.collection_id === collection.id
                    );
                    const displayValue = hasAnyPoints
                      ? (colData?.total_points ?? 0)
                      : (colData?.nft_count ?? 0);
                    return (
                      <td
                        key={collection.id}
                        className="py-3 px-4 text-center"
                        style={{ minWidth: '70px', maxWidth: '140px' }}
                      >
                        <span
                          className={`text-body font-body ${
                            displayValue > 0 ? 'text-default-font' : 'text-subtext-color'
                          }`}
                        >
                          {displayValue > 0 ? formatPointValue(displayValue) : '-'}
                        </span>
                      </td>
                    );
                  })}
                <td className="py-3 px-4 text-center w-20">
                  <span className="text-body-bold font-body-bold text-default-font">
                    {holder.total_nft_count.toLocaleString()}
                  </span>
                </td>
                {hasAnyPoints && (
                  <td className="py-3 px-4 text-center w-24">
                    <span className="text-body-bold font-body-bold text-default-font">
                      {formatPointValue(holder.total_points)}
                    </span>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
